'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { storyKeys } from '../queryKeys';
import { subscriptionKeys, PRICING_DEFAULTS } from '@/contexts/subscriptions';
import type { TokenPricingData } from '@/contexts/subscriptions';
import { regenerateBlock } from '../actions/generation';

/**
 * Optimistic balance deduction helper.
 * Reads current balance from cache, subtracts cost, returns previous value for rollback.
 */
function optimisticDeductBalance(
  queryClient: ReturnType<typeof useQueryClient>,
  familyId: string,
  cost: number,
) {
  const balanceKey = subscriptionKeys.balance(familyId);
  const previousBalance = queryClient.getQueryData<{ balance: number }>(balanceKey);

  if (previousBalance) {
    queryClient.setQueryData(balanceKey, {
      ...previousBalance,
      balance: Math.max(0, previousBalance.balance - cost),
    });
  }

  return previousBalance;
}

export function useGenerationMutations(storyId: string, familyId?: string | null) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: storyKeys.detail(storyId) });

  const regenerate = useMutation({
    mutationFn: (params: { blockId: string; context?: string }) =>
      regenerateBlock(storyId, params.blockId, params.context),
    onMutate: async () => {
      if (!familyId) return;
      await queryClient.cancelQueries({ queryKey: subscriptionKeys.balance(familyId) });
      const pricing = queryClient.getQueryData<TokenPricingData>(subscriptionKeys.pricing());
      const cost = pricing?.blockTextReservation ?? PRICING_DEFAULTS.blockTextReservation;
      const previousBalance = optimisticDeductBalance(queryClient, familyId, cost);
      return { previousBalance };
    },
    onError: (_err, _vars, context) => {
      if (familyId && context?.previousBalance) {
        queryClient.setQueryData(subscriptionKeys.balance(familyId), context.previousBalance);
      }
    },
    onSuccess: invalidate,
    onSettled: () => {
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.balance(familyId) });
      }
    },
  });

  return {
    regenerateBlock: regenerate,
  };
}
